import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";

const RANGES = ["1h", "24h", "7d", "30d"];
const SERIES = [
  { key: "cpu", label: "CPU", color: "#E11D2A" },
  { key: "ram", label: "Memória", color: "#3B82F6" },
  { key: "disk", label: "Disco", color: "#22C55E" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-popover/95 px-3 py-2 shadow-xl backdrop-blur">
      <p className="mb-1 font-mono text-[11px] text-muted-foreground">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{SERIES.find((s) => s.key === p.dataKey)?.label}</span>
          <span className="ml-auto font-mono font-medium text-foreground">{p.value}%</span>
        </p>
      ))}
    </div>
  );
};

// Gráfico de área multi-série com seletor de intervalo.
export const MetricChart = ({ dataByRange, defaultRange = "24h", height = 280, keys = ["cpu", "ram", "disk"] }) => {
  const [range, setRange] = useState(defaultRange);
  const data = dataByRange?.[range] || [];
  const series = SERIES.filter((s) => keys.includes(s.key));

  if (!data.length) return <p className="text-sm text-muted-foreground">Histórico não coletado. Consulte o snapshot atual dos servidores.</p>;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-0.5 rounded-lg border border-line bg-black/20 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`chart-range-${r}`}
              onClick={() => setRange(r)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                range === r ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 6, left: -18, bottom: 0 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.1)" }} />
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#grad-${s.key})`}
                dot={false}
                isAnimationActive={false}
                activeDot={{ r: 3, strokeWidth: 0 }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
